import { twoToThreeCountryCode } from '@intigral/constant';
import { isBrowser } from './browser';
import { ZodSchema } from 'zod';

/*
 * Get last part of the URL
 * @param {url} string
 * @returns {string}
 */
export const getLastPartOfUrl = (url: string | null): string | null => {
    if (url === null || url === '' || isValidUrl(url) === false) {
        return null;
    }
    // Remove any trailing slashes
    url = url.replace(/\/+$/, '');
    // Remove the protocol
    url = url.replace(/^https?:\/\//, '');
    // Split the URL by '/'
    const parts = url.split('/');
    if (parts.length === 1) {
        return null;
    }
    // Get the last element (which is the last path)
    return parts[parts.length - 1] || null;
};

/**
 * Check if the given string is a valid URL
 * @param {string | null} url
 * @returns {boolean}
 */
export const isValidUrl = (url: string | null): boolean => {
    if (url === null || url === '') {
        return false;
    }

    try {
        new URL(url);
        return true;
    } catch (e) {
        return false;
    }
};

/**
 * Get country flag url
 * @param {string} countryCode Two character country code
 * @param {string} ext Image file extension: svg or png (default: svg)
 * @returns Return the full image URL.
 */
export const getCountryFlagUrl = (countryCode: string, ext: 'svg' | 'png' = 'svg'): string => {
    countryCode = countryCode.toLowerCase();
    if (Object.keys(twoToThreeCountryCode).includes(countryCode) === false) {
        return '';
    }
    const path = process.env['NX_COUNTRY_FLAG_PATH'] || '/assets/countries/';
    return path.replace(/\/$/, '') + '/' + countryCode + '.' + ext;
};

export const getQueryParam = (param: string, schema: ZodSchema) => {
    if (!isBrowser()) {
        return null;
    }
    const url = new URL(window.location.href);
    let queryParams;

    // Check if query parameters are within the hash
    if (url.hash.includes('?')) {
        const hashQueryParams = new URLSearchParams(url.hash.split('?')[1]);
        queryParams = Object.fromEntries(hashQueryParams.entries());
    } else {
        queryParams = Object.fromEntries(new URLSearchParams(url.search).entries());
    }
    //if param is empty return all query params
    if (param == '') {
        return new URLSearchParams(queryParams).toString();
    }
    const parsedParams = schema.safeParse(queryParams);
    if (!parsedParams.success) {
        // Handle validation errors
        // console.error('Query param Error: ', parsedParams?.error?.errors[0]?.message);
        return null;
    }

    return parsedParams.data[param] || null;
};

export function getParam(url: string, param: string): string | null {
    // Parse the URL normally
    const parsedUrl = new URL(url, window.location.origin);
    const params = new URLSearchParams(parsedUrl.search);

    // Check if the parameter is in the standard query string
    let value = params.get(param);

    if (value === null) {
        // If not found, check in the hash part of the URL
        const hash = parsedUrl.hash.slice(1); // Remove the leading '#'

        // Check if the hash contains a '?' indicating the start of query parameters
        const hashQueryIndex = hash.indexOf('?');
        if (hashQueryIndex !== -1) {
            // Extract the query string from the hash
            const hashQueryString = hash.substring(hashQueryIndex + 1);
            const hashParams = new URLSearchParams(hashQueryString);
            value = hashParams.get(param);
        }
    }

    return value;
}

export function generateQueryStringFromQueryParams(
    searchParams: Record<string, string> | URLSearchParams | { entries(): IterableIterator<[string, string]> } | null | undefined,
    excludeParamKeys: string[]
): string {
    const filteredParams = new URLSearchParams();

    if (!searchParams) {
        return '';
    }

    const entries =
        'entries' in searchParams && typeof searchParams.entries === 'function'
            ? Array.from(searchParams.entries())
            : Object.entries(searchParams);

    for (const [key, value] of entries) {
        if (!excludeParamKeys.includes(key)) {
            filteredParams.append(key, value);
        }
    }
    const str = filteredParams.toString();
    return str ? `&${str}` : '';
}

/**
 * Removes the language prefix (e.g., "/en" or "/ar") from a given pathname.
 * This is useful when you want to get the pathname without the language code.
 *
 * @param {string} pathname - The URL path that may contain a language prefix (e.g., "/en/home").
 * @returns {string} Returns the pathname without the language prefix (e.g., "home").
 */
export const getPathNameWithoutLangPrefix = (pathname = ''): string => {
    if (pathname) {
        return pathname.replace(/^(\/(en|ar))?(\/|$)/i, '');
    }
    return '';
};

export const truncateDomain = (url: string) => {
    try {
        // Login stores the raw `redirect` query value, which can be a bare path such as "account".
        const currentOrigin = window.location.origin;
        const urlObj = new URL(url, currentOrigin);
        const urlOrigin = urlObj.origin;
        // Check if the origin of the URL matches the current page's origin
        if (urlOrigin === currentOrigin) {
            // If they match, return the path and query part (exclude the domain)
            return urlObj.pathname + urlObj.search;
        } else {
            // If the origins don't match, return the full URL
            return url;
        }
    } catch (error) {
        console.error('truncateDomain error', error);
        return null;
    }
};

/**
 * Updates or adds query parameters in a query string.
 *
 * @param query - The original query string (e.g., "key1=val1&key2=val2").
 * @param updates - An object where keys are the query parameters to update or add, and values are the new values.
 * @returns A new query string with the updated and/or added parameters.
 *
 * @example
 * const query = "defaultOperator=CC_KSA_FVP&tierGuid=PLUS_TIER";
 * const updates = { defaultOperator: "NEW_OPERATOR", username: "kiran@yopmail.com" };
 * const result = updateQueryParams(query, updates);
 * console.log(result);
 * // Output: "defaultOperator=NEW_OPERATOR&tierGuid=PLUS_TIER&username=kiran@yopmail.com"
 */
export const updateQueryParams = (
    query: string,
    updates: Record<string, string>,
    removeKeys: string[] = []
): string => {
    // split query string into key-value pairs
    const params: [string, string][] = query
        ? query.split('&').map((p) => {
              const [key, value] = p.split('=');
              return [key, value] as [string, string]; // cast to tuple
          })
        : [];

    const map = new Map(params);

    // update existing keys or add new ones
    Object.entries(updates).forEach(([key, value]) => {
        map.set(key, value);
    });

    // remove keys
    removeKeys.forEach((key) => {
        map.delete(key);
    });

    // convert back to query string
    return Array.from(map.entries())
        .map(([key, value]) => `${key}=${value}`)
        .join('&');
};
